/**
 * AssetFactory —— 用 Phaser.Graphics 过程化生成游戏纹理
 *
 * 设计意图：原型阶段不依赖任何 PNG/SVG 资源，但视觉风格往参考图靠：
 *   - 暖色块 + 深棕描边 + 圆润形状（"卡通"感）
 *   - 留出后续替换：每张纹理都注册成稳定 key，将来用 loader.image(key, url)
 *     在 preload 早于这些 generateTexture 调用就行
 *
 * 当前生成：
 *   'player-tank'          深灰底 + 蓝色轮子的车身
 *   'frog-base'            基础青蛙
 *   'frog-cool'            戴墨镜的酷青蛙
 *   'frog-cry'             笑哭青蛙（眼睛弯月 + 泪滴）
 *   'frog-sad'             委屈青蛙（V 形眉 + 嘴角向下）
 *   'frog-yeah'            比耶青蛙（小手 V 字）
 *   'frog-pout'            嘟嘴青蛙（小圆嘴）
 *   FROG_KEYS              所有青蛙 key 列表，Spawner 用来随机抽
 *   'chest-gold'           金边木箱
 *   'grass-tile'           战场泥地/焦土滚动背景
 *   'star'                 背景烟尘光点
 *   'particle'             4×4 白色方块（爆炸碎屑）
 *   'bullet'               金黄色圆形子弹
 *   'boss-bullet'          红色带描边圆形（Boss 投射物）
 *
 * 所有尺寸都基于 GameConfig 里的实体尺寸，保持视觉与碰撞框对齐。
 */
import Phaser from 'phaser';
import {
  PLAYER,
  BULLET,
  BOSS,
} from '../config/GameConfig';

/** 6 种小怪变体的 key 列表 —— Spawner 从这里随机挑一个 */
export const MONSTER_KEYS: readonly string[] = [
  'mob-straw',     // 草帽（基础原型）
  'mob-cool',      // 草帽 + 墨镜
  'mob-cap-red',   // 红色棒球帽
  'mob-cap-blue',  // 蓝色棒球帽
  'mob-blush',     // 害羞（无帽 + 大腮红）
  'mob-tongue',    // 吐舌头
];

/**
 * 怪物贴图源尺寸（绘制分辨率）—— 故意比 ENEMY.width/height 大，
 * 这样运行时 Enemy 用 setDisplaySize 缩放后仍然清晰。
 * 这两个常量与 ENEMY.width/height 解耦：物理框跟着 ENEMY 配置走，
 * 视觉清晰度跟着这里走。
 */
const MOB_TEX_W = 110;
const MOB_TEX_H = 80;
const CHEST_TEX_W = 100;
const CHEST_TEX_H = 80;

/**
 * 由 Scene.preload 调用一次。所有纹理注册到 scene.textures。
 * 重复调用是安全的（覆盖式）。
 */
export function buildAllTextures(scene: Phaser.Scene): void {
  // —— 通用小素材 ——
  buildBulletTexture(scene);
  buildBossBulletTexture(scene);
  buildParticleTexture(scene);
  buildStarTexture(scene);
  // —— 角色 / 物体 / 地块 ——
  buildPlayerTankTexture(scene);
  buildAllMonsterTextures(scene);
  buildBossMonsterTexture(scene);
  buildChestGoldTexture(scene);
  buildBuffChestTextures(scene);
  buildGrassTileTexture(scene);
  buildObstacleTextures(scene);
}

// ─── 子弹 / 粒子 / 星点 ───────────────────────────────────────────────

/**
 * 子弹头：上方尖头 + 金黄胶囊弹身 + 棕色描边 + 白色高光
 *
 * 画法（自上而下）：
 *   1) 弹头：等腰三角形，浅金色 + 白色高光斜条
 *   2) 弹身：圆角矩形，主金色 + 描边
 *   3) 弹底：底部一条更深棕色的"弹壳缘"
 * 输出尺寸 = (BULLET.textureWidth, BULLET.textureHeight)，
 * 视觉中心约在贴图中央，仍走 setCircle(BULLET.radius) 的物理体（命中手感不变）
 */
function buildBulletTexture(scene: Phaser.Scene): void {
  const w = BULLET.textureWidth;   // 12
  const h = BULLET.textureHeight;  // 22
  const g = scene.make.graphics({ x: 0, y: 0 });

  const main = BULLET.color;        // 0xffd166
  const headLight = 0xffe89a;       // 弹头亮色
  const outline = 0x6a4a10;
  const baseDark = 0x9a6f1d;

  // 弹身的高度占比 ~ 0.6，弹头 0.32，弹底 0.08
  const headH = h * 0.34;
  const baseH = h * 0.10;
  const bodyTop = headH;
  const bodyH = h - headH - baseH;

  // —— 1) 弹身（圆角矩形） ——
  g.fillStyle(main, 1);
  g.fillRoundedRect(1, bodyTop, w - 2, bodyH, 3);
  g.lineStyle(1.2, outline, 1);
  g.strokeRoundedRect(1, bodyTop, w - 2, bodyH, 3);

  // 弹身白色高光（左侧细竖条）
  g.fillStyle(0xffffff, 0.55);
  g.fillRect(2.5, bodyTop + 2, 1.5, bodyH - 4);

  // —— 2) 弹头（三角形，从弹身顶端两点向上汇聚到中心顶点） ——
  g.fillStyle(headLight, 1);
  g.fillTriangle(
    1,         bodyTop + 1,         // 左下
    w - 1,     bodyTop + 1,         // 右下
    w / 2,     1,                   // 顶点（最上）
  );
  g.lineStyle(1.2, outline, 1);
  g.strokeTriangle(
    1,         bodyTop + 1,
    w - 1,     bodyTop + 1,
    w / 2,     1,
  );
  // 弹头小高光（左下到右上的小白条）
  g.fillStyle(0xffffff, 0.7);
  g.fillTriangle(
    w / 2 - 1, headH - 2,
    w / 2 + 1, headH - 2,
    w / 2,     headH * 0.45,
  );

  // —— 3) 弹底（深棕色弹壳缘） ——
  g.fillStyle(baseDark, 1);
  g.fillRoundedRect(0, h - baseH, w, baseH, 2);
  g.lineStyle(1, outline, 1);
  g.strokeRoundedRect(0, h - baseH, w, baseH, 2);

  g.generateTexture('bullet', w, h);
  g.destroy();
}

function buildBossBulletTexture(scene: Phaser.Scene): void {
  const r = BOSS.projectileRadius;
  const g = scene.make.graphics({ x: 0, y: 0 });
  g.fillStyle(BOSS.projectileColor, 1);
  g.fillCircle(r, r, r);
  g.lineStyle(2, 0xffffff, 0.9);
  g.strokeCircle(r, r, r - 1);
  g.generateTexture('boss-bullet', r * 2, r * 2);
  g.destroy();
}

function buildParticleTexture(scene: Phaser.Scene): void {
  const g = scene.make.graphics({ x: 0, y: 0 });
  g.fillStyle(0xffffff, 1);
  g.fillRect(0, 0, 4, 4);
  g.generateTexture('particle', 4, 4);
  g.destroy();
}

function buildStarTexture(scene: Phaser.Scene): void {
  const g = scene.make.graphics({ x: 0, y: 0 });
  g.fillStyle(0xffffff, 1);
  g.fillRect(0, 0, 2, 2);
  g.generateTexture('star', 2, 2);
  g.destroy();
}

// ─── 玩家坦克 ───────────────────────────────────────────────────────
//
// 参考图 2 下方：浅灰底盘 + 大蓝胎纹轮 + 顶部炮塔
// 我们把"两个炮口"的细节也画上，呼应"双管"主题
function buildPlayerTankTexture(scene: Phaser.Scene): void {
  const w = PLAYER.width; // 56
  const h = PLAYER.height; // 64
  const g = scene.make.graphics({ x: 0, y: 0 });

  const outline = 0x2a2a2a;

  // —— 轮子（左右，画在底层） ——
  const wheelR = h * 0.18;
  const wheelY = h - wheelR - 1;
  g.fillStyle(0x222222, 1);
  g.fillCircle(wheelR + 1, wheelY, wheelR);
  g.fillCircle(w - wheelR - 1, wheelY, wheelR);
  // 蓝色轮毂
  g.fillStyle(0x4cd2ff, 1);
  g.fillCircle(wheelR + 1, wheelY, wheelR * 0.55);
  g.fillCircle(w - wheelR - 1, wheelY, wheelR * 0.55);
  g.fillStyle(0x0a1830, 1);
  g.fillCircle(wheelR + 1, wheelY, wheelR * 0.18);
  g.fillCircle(w - wheelR - 1, wheelY, wheelR * 0.18);

  // —— 主车身（圆角矩形） ——
  const bodyTop = h * 0.28;
  const bodyH = h - bodyTop - wheelR * 1.4;
  g.fillStyle(0xb8bcc4, 1); // 浅灰
  g.fillRoundedRect(2, bodyTop, w - 4, bodyH, 8);
  g.lineStyle(2, outline, 1);
  g.strokeRoundedRect(2, bodyTop, w - 4, bodyH, 8);

  // 车身上的两个深色舷窗
  g.fillStyle(0x4a4f58, 1);
  g.fillRoundedRect(8, bodyTop + bodyH * 0.45, (w - 16) * 0.4, bodyH * 0.4, 3);
  g.fillRoundedRect(w - 8 - (w - 16) * 0.4, bodyTop + bodyH * 0.45, (w - 16) * 0.4, bodyH * 0.4, 3);

  // —— 炮塔（顶部） ——
  const turretW = w * 0.5;
  const turretH = bodyTop;
  const turretX = (w - turretW) / 2;
  g.fillStyle(0x6a6f78, 1);
  g.fillRoundedRect(turretX, 4, turretW, turretH - 4, 6);
  g.lineStyle(2, outline, 1);
  g.strokeRoundedRect(turretX, 4, turretW, turretH - 4, 6);

  // 双炮口（参考图里头顶的子弹+小人）
  g.fillStyle(0xffd166, 1); // 金黄子弹
  g.fillCircle(turretX + turretW * 0.32, turretH - 2, 3);
  g.fillStyle(0xffaa44, 1);
  g.fillCircle(turretX + turretW * 0.68, turretH - 2, 3);

  g.generateTexture('player-tank', w, h);
  g.destroy();
}

// ─── 黄胖小怪系列（6 种装扮/表情） ───────────────────────────────────
//
// 参考图：圆胖椭圆黄身 + 深棕描边 + 草帽（红条） + 黑眼 + 腮红 + 一颗小牙
// 6 个变体共用 _drawMonsterBody 画身体，再叠不同的帽子和表情。

interface MonsterFace {
  hat: 'straw' | 'cap-red' | 'cap-blue' | 'none';
  eyes: 'normal' | 'closed' | 'sparkle';
  mouth: 'tooth' | 'smile' | 'tongue';
  extra?: 'sunglasses' | 'big-blush';
}

function buildAllMonsterTextures(scene: Phaser.Scene): void {
  _drawMonster(scene, 'mob-straw',    { hat: 'straw',    eyes: 'normal', mouth: 'tooth' });
  _drawMonster(scene, 'mob-cool',     { hat: 'straw',    eyes: 'normal', mouth: 'smile', extra: 'sunglasses' });
  _drawMonster(scene, 'mob-cap-red',  { hat: 'cap-red',  eyes: 'normal', mouth: 'tooth' });
  _drawMonster(scene, 'mob-cap-blue', { hat: 'cap-blue', eyes: 'sparkle', mouth: 'smile' });
  _drawMonster(scene, 'mob-blush',    { hat: 'none',     eyes: 'closed', mouth: 'smile', extra: 'big-blush' });
  _drawMonster(scene, 'mob-tongue',   { hat: 'straw',    eyes: 'normal', mouth: 'tongue' });
}

function _drawMonster(scene: Phaser.Scene, key: string, face: MonsterFace): void {
  const w = MOB_TEX_W;
  const h = MOB_TEX_H;
  const g = scene.make.graphics({ x: 0, y: 0 });

  const outline = 0x4a3010;     // 深棕描边
  const skin = 0xffd34a;        // 主体黄
  const skinShadow = 0xe6b22a;  // 偏暗黄（底部阴影色）
  const cheek = 0xff8a8a;
  const dark = 0x1a1a1a;
  const white = 0xffffff;

  // —— 身体（圆胖椭圆 + 底部暗调 + 圆点斑） ——
  // 身体重心整体下移，给头顶留位置戴帽子
  const bodyW = w * 0.78;
  const bodyH = h * 0.78;
  const cx = w / 2;
  const cy = h - bodyH / 2 - 2;

  // 主椭圆
  g.fillStyle(skin, 1);
  g.fillEllipse(cx, cy, bodyW, bodyH);
  // 底部 1/3 暗一点（增加体积感）
  g.fillStyle(skinShadow, 0.6);
  g.fillEllipse(cx, cy + bodyH * 0.18, bodyW * 0.92, bodyH * 0.5);
  // 棕色描边
  g.lineStyle(2.5, outline, 1);
  g.strokeEllipse(cx, cy, bodyW, bodyH);

  // 一些雀斑/腮红斑（参考图肚子上的小点点）
  g.fillStyle(skinShadow, 0.7);
  g.fillCircle(cx - bodyW * 0.18, cy + bodyH * 0.22, 2);
  g.fillCircle(cx + bodyW * 0.05, cy + bodyH * 0.28, 2.4);
  g.fillCircle(cx + bodyW * 0.22, cy + bodyH * 0.18, 1.8);

  // —— 眼睛 ——
  const eyeY = cy - bodyH * 0.18;
  const eyeDx = bodyW * 0.16;

  if (face.eyes === 'normal' || face.eyes === 'sparkle') {
    g.fillStyle(dark, 1);
    g.fillCircle(cx - eyeDx, eyeY, 3.2);
    g.fillCircle(cx + eyeDx, eyeY, 3.2);
    // 高光小白点
    g.fillStyle(white, 1);
    g.fillCircle(cx - eyeDx + 1.2, eyeY - 1.2, 1.2);
    g.fillCircle(cx + eyeDx + 1.2, eyeY - 1.2, 1.2);
    if (face.eyes === 'sparkle') {
      // 额外一颗小星点高光，眼睛闪闪发光的感觉
      g.fillCircle(cx - eyeDx - 1, eyeY + 1, 0.6);
      g.fillCircle(cx + eyeDx - 1, eyeY + 1, 0.6);
    }
  } else if (face.eyes === 'closed') {
    // 一对弯月（^ ^）
    g.lineStyle(2, outline, 1);
    g.beginPath();
    g.arc(cx - eyeDx, eyeY + 1, 3.5, 1.05 * Math.PI, 1.95 * Math.PI, false);
    g.arc(cx + eyeDx, eyeY + 1, 3.5, 1.05 * Math.PI, 1.95 * Math.PI, false);
    g.strokePath();
  }

  // —— 腮红（参考图就是两块小红圆） ——
  const blushSize = face.extra === 'big-blush' ? 6.5 : 4.5;
  const blushAlpha = face.extra === 'big-blush' ? 0.95 : 0.85;
  g.fillStyle(cheek, blushAlpha);
  g.fillEllipse(cx - bodyW * 0.27, eyeY + 6, blushSize, blushSize * 0.7);
  g.fillEllipse(cx + bodyW * 0.27, eyeY + 6, blushSize, blushSize * 0.7);

  // —— 嘴 + 小牙 ——
  const mouthY = eyeY + 11;
  if (face.mouth === 'tooth') {
    // 短横线 + 右侧一颗小白牙
    g.lineStyle(1.8, outline, 1);
    g.beginPath();
    g.moveTo(cx - 5, mouthY);
    g.lineTo(cx + 5, mouthY);
    g.strokePath();
    g.fillStyle(white, 1);
    g.fillRect(cx + 1, mouthY, 2, 3);
    g.lineStyle(1, outline, 1);
    g.strokeRect(cx + 1, mouthY, 2, 3);
  } else if (face.mouth === 'smile') {
    g.lineStyle(2, outline, 1);
    g.beginPath();
    g.arc(cx, mouthY - 2, 5, 0.15 * Math.PI, 0.85 * Math.PI, false);
    g.strokePath();
  } else if (face.mouth === 'tongue') {
    // 张嘴吐粉色舌头
    g.fillStyle(0x6a2828, 1);
    g.fillEllipse(cx, mouthY, 8, 4);
    g.lineStyle(1.5, outline, 1);
    g.strokeEllipse(cx, mouthY, 8, 4);
    g.fillStyle(0xff8a8a, 1);
    g.fillEllipse(cx + 1, mouthY + 2, 5, 3);
  }

  // —— 帽子 ——
  // 注意：先画身体后画帽子，让帽子盖住头顶弧线
  if (face.hat === 'straw') {
    _drawStrawHat(g, cx, cy - bodyH * 0.5 + 4, bodyW, h, outline);
  } else if (face.hat === 'cap-red') {
    _drawBaseballCap(g, cx, cy - bodyH * 0.5 + 4, bodyW, h, outline, 0xd94545);
  } else if (face.hat === 'cap-blue') {
    _drawBaseballCap(g, cx, cy - bodyH * 0.5 + 4, bodyW, h, outline, 0x4cd2ff);
  }

  // —— 附件：墨镜（盖在眼睛上） ——
  if (face.extra === 'sunglasses') {
    const glW = 14;
    const glH = 9;
    g.fillStyle(dark, 1);
    g.fillRoundedRect(cx - eyeDx - glW / 2, eyeY - glH / 2, glW, glH, 3);
    g.fillRoundedRect(cx + eyeDx - glW / 2, eyeY - glH / 2, glW, glH, 3);
    // 桥
    g.fillRect(cx - eyeDx + glW / 2 - 1, eyeY - 1.5, eyeDx * 2 - glW + 2, 2.5);
    // 反光
    g.fillStyle(white, 0.7);
    g.fillRect(cx - eyeDx - glW / 2 + 2, eyeY - glH / 2 + 1.5, 4, 1.5);
    g.fillRect(cx + eyeDx - glW / 2 + 2, eyeY - glH / 2 + 1.5, 4, 1.5);
  }

  g.generateTexture(key, w, h);
  g.destroy();
}

/**
 * 画草帽：扁椭圆帽檐 + 梯形帽冠 + 红色绑带
 * @param {Phaser.GameObjects.Graphics} g
 * @param {number} cx 头顶中心 x
 * @param {number} hatBaseY 帽檐基线 y（小怪头顶位置）
 * @param {number} bodyW
 * @param {number} h
 * @param {number} outline
 */
function _drawStrawHat(g: Phaser.GameObjects.Graphics, cx: number, hatBaseY: number, bodyW: number, h: number, outline: number): void {
  const brimW = bodyW * 1.0;
  const brimH = h * 0.18;
  const topW = bodyW * 0.45;
  const topH = h * 0.22;
  const strawColor = 0xe6c46a;
  const strawShadow = 0xb8923a;

  // 帽檐
  g.fillStyle(strawColor, 1);
  g.fillEllipse(cx, hatBaseY, brimW, brimH);
  // 帽檐底部阴影（下半弧偏暗）
  g.fillStyle(strawShadow, 0.55);
  g.fillEllipse(cx, hatBaseY + brimH * 0.18, brimW * 0.95, brimH * 0.5);
  g.lineStyle(2.5, outline, 1);
  g.strokeEllipse(cx, hatBaseY, brimW, brimH);

  // 帽冠（梯形 fillPoints，注意顺时针/逆时针都能 fill）
  const topY = hatBaseY - topH;
  const points = [
    { x: cx - topW / 2 - 2, y: hatBaseY - 2 }, // 底左
    { x: cx + topW / 2 + 2, y: hatBaseY - 2 }, // 底右
    { x: cx + topW / 2 - 6, y: topY },         // 顶右
    { x: cx - topW / 2 + 6, y: topY },         // 顶左
  ];
  g.fillStyle(strawColor, 1);
  g.fillPoints(points, true);
  g.lineStyle(2.5, outline, 1);
  g.strokePoints(points, true);

  // 草纹（几条小斜线）
  g.lineStyle(1, strawShadow, 0.6);
  for (let i = 0; i < 4; i++) {
    const x = cx - topW / 3 + i * (topW / 4);
    g.beginPath();
    g.moveTo(x, topY + 4);
    g.lineTo(x + 3, topY + topH - 4);
    g.strokePath();
  }

  // 红色绑带
  g.fillStyle(0xd94545, 1);
  g.fillRect(cx - topW / 2 - 1, hatBaseY - 5, topW + 2, 4);
  g.lineStyle(1, outline, 1);
  g.strokeRect(cx - topW / 2 - 1, hatBaseY - 5, topW + 2, 4);
}

/**
 * 画棒球帽：半椭圆帽冠 + 帽檐
 * @param {number} mainColor 帽顶颜色（红 / 蓝）
 */
function _drawBaseballCap(g: Phaser.GameObjects.Graphics, cx: number, hatBaseY: number, bodyW: number, h: number, outline: number, mainColor: number): void {
  const capW = bodyW * 0.85;
  const capH = h * 0.32;
  const brimW = bodyW * 0.7;

  // 帽冠（实心半椭圆）：用一个稍高的椭圆 + 用身体颜色覆盖下半（这里用 fillEllipse 后 strokePath 即可，因为下方有身体盖住）
  g.fillStyle(mainColor, 1);
  g.fillEllipse(cx, hatBaseY, capW, capH);
  // 高光：浅色椭圆叠在顶部
  g.fillStyle(0xffffff, 0.25);
  g.fillEllipse(cx - capW * 0.12, hatBaseY - capH * 0.18, capW * 0.55, capH * 0.4);
  g.lineStyle(2.5, outline, 1);
  g.strokeEllipse(cx, hatBaseY, capW, capH);

  // 帽檐（一个偏右下的椭圆，做出朝右的"鸭舌"效果）
  g.fillStyle(mainColor, 1);
  g.fillEllipse(cx + brimW * 0.25, hatBaseY + capH * 0.12, brimW, h * 0.12);
  g.lineStyle(2.5, outline, 1);
  g.strokeEllipse(cx + brimW * 0.25, hatBaseY + capH * 0.12, brimW, h * 0.12);

  // 中央纽扣
  g.fillStyle(outline, 1);
  g.fillCircle(cx, hatBaseY - capH * 0.35, 1.8);
}

// ─── Boss 小怪（戴皇冠的胖大佬） ─────────────────────────────────────
//
// 用 BOSS.width × BOSS.height 大尺寸（默认 240×110），整体宽扁，
// 头顶一顶五尖皇冠 + 横眉 + 大胡子 + 锐利尖牙，区分于普通小怪
function buildBossMonsterTexture(scene: Phaser.Scene): void {
  const w = BOSS.width;   // 240
  const h = BOSS.height;  // 110
  const g = scene.make.graphics({ x: 0, y: 0 });

  const outline = 0x4a3010;
  // Boss 主色比普通小怪深一点（更"老练"），略偏橙
  const skin = 0xffb24a;
  const skinShadow = 0xd07820;
  const skinHl = 0xffd97a;
  const cheek = 0xff8a8a;
  const dark = 0x1a1a1a;
  const white = 0xffffff;

  // 身体：宽扁椭圆
  const cx = w / 2;
  const cy = h * 0.62;
  const bodyW = w * 0.86;
  const bodyH = h * 0.78;

  g.fillStyle(skin, 1);
  g.fillEllipse(cx, cy, bodyW, bodyH);
  // 顶部高光
  g.fillStyle(skinHl, 0.45);
  g.fillEllipse(cx, cy - bodyH * 0.22, bodyW * 0.85, bodyH * 0.5);
  // 底部阴影
  g.fillStyle(skinShadow, 0.5);
  g.fillEllipse(cx, cy + bodyH * 0.2, bodyW * 0.92, bodyH * 0.45);
  g.lineStyle(3, outline, 1);
  g.strokeEllipse(cx, cy, bodyW, bodyH);

  // 肚子斑点
  g.fillStyle(skinShadow, 0.7);
  g.fillCircle(cx - bodyW * 0.22, cy + bodyH * 0.22, 3);
  g.fillCircle(cx - bodyW * 0.06, cy + bodyH * 0.30, 4);
  g.fillCircle(cx + bodyW * 0.10, cy + bodyH * 0.26, 3);
  g.fillCircle(cx + bodyW * 0.26, cy + bodyH * 0.20, 2.5);

  // —— 横眉（愤怒）+ 眼睛 ——
  const eyeY = cy - bodyH * 0.18;
  const eyeDx = bodyW * 0.16;
  // 眉毛（粗斜线）
  g.lineStyle(4, outline, 1);
  g.beginPath();
  // 左眉：从外上到内下（\）
  g.moveTo(cx - eyeDx - 10, eyeY - 9);
  g.lineTo(cx - eyeDx + 6, eyeY - 4);
  // 右眉：从内下到外上（/）
  g.moveTo(cx + eyeDx - 6, eyeY - 4);
  g.lineTo(cx + eyeDx + 10, eyeY - 9);
  g.strokePath();

  // 眼睛（红色虹膜，更凶）
  g.fillStyle(white, 1);
  g.fillCircle(cx - eyeDx, eyeY, 7);
  g.fillCircle(cx + eyeDx, eyeY, 7);
  g.lineStyle(1.5, outline, 1);
  g.strokeCircle(cx - eyeDx, eyeY, 7);
  g.strokeCircle(cx + eyeDx, eyeY, 7);
  g.fillStyle(0xd94545, 1);
  g.fillCircle(cx - eyeDx, eyeY, 3.5);
  g.fillCircle(cx + eyeDx, eyeY, 3.5);
  g.fillStyle(dark, 1);
  g.fillCircle(cx - eyeDx, eyeY, 1.8);
  g.fillCircle(cx + eyeDx, eyeY, 1.8);
  // 高光
  g.fillStyle(white, 1);
  g.fillCircle(cx - eyeDx + 2, eyeY - 2, 1.4);
  g.fillCircle(cx + eyeDx + 2, eyeY - 2, 1.4);

  // —— 腮红（小一点，毕竟是反派） ——
  g.fillStyle(cheek, 0.7);
  g.fillEllipse(cx - bodyW * 0.30, eyeY + 8, 8, 4);
  g.fillEllipse(cx + bodyW * 0.30, eyeY + 8, 8, 4);

  // —— 大嘴 + 两颗尖牙 ——
  const mouthY = eyeY + 16;
  const mouthW = 28;
  // 嘴线（向下弧，邪笑）—— 用 arc 半圆代替二次贝塞尔（Phaser.Graphics 没有 quadraticCurveTo）
  g.lineStyle(2.5, outline, 1);
  g.beginPath();
  g.arc(cx, mouthY, mouthW / 2, 0, Math.PI, false);
  g.strokePath();
  // 尖牙（左右各一）
  g.fillStyle(white, 1);
  g.fillTriangle(
    cx - 8, mouthY,
    cx - 4, mouthY,
    cx - 6, mouthY + 6,
  );
  g.fillTriangle(
    cx + 4, mouthY,
    cx + 8, mouthY,
    cx + 6, mouthY + 6,
  );
  g.lineStyle(1, outline, 1);
  g.strokeTriangle(
    cx - 8, mouthY,
    cx - 4, mouthY,
    cx - 6, mouthY + 6,
  );
  g.strokeTriangle(
    cx + 4, mouthY,
    cx + 8, mouthY,
    cx + 6, mouthY + 6,
  );

  // —— 皇冠（5 尖 + 红宝石 + 镶边） ——
  // 皇冠基座 y：身体顶部边缘上方
  const crownBaseY = cy - bodyH * 0.5 + 4;
  _drawCrown(g, cx, crownBaseY, bodyW * 0.5, h * 0.34, outline);

  g.generateTexture('mob-boss', w, h);
  g.destroy();
}

/**
 * 画皇冠：底座条 + 5 个尖（中间最高）+ 每尖顶宝石
 */
function _drawCrown(g: Phaser.GameObjects.Graphics, cx: number, baseY: number, w: number, h: number, outline: number): void {
  const gold = 0xffd166;
  const goldDark = 0xc09020;
  const ruby = 0xd94545;
  const sapph = 0x4cd2ff;

  const baseH = h * 0.32;
  const tipsTop = baseY - h;

  // 5 个尖（顶点）
  const peaks = [
    { x: cx - w / 2, y: baseY - h * 0.55 },         // 左外
    { x: cx - w / 4, y: baseY - h * 0.85 },         // 左内
    { x: cx,         y: tipsTop },                  // 中（最高）
    { x: cx + w / 4, y: baseY - h * 0.85 },
    { x: cx + w / 2, y: baseY - h * 0.55 },
  ];

  // 用 fillPoints 围成一个"齿状"形状：底左 → 各峰之间的谷 + 峰 → 底右
  const pts = [
    { x: cx - w / 2, y: baseY }, // 底左
    peaks[0],
    { x: (peaks[0].x + peaks[1].x) / 2, y: baseY - h * 0.25 }, // 谷
    peaks[1],
    { x: (peaks[1].x + peaks[2].x) / 2, y: baseY - h * 0.35 },
    peaks[2],
    { x: (peaks[2].x + peaks[3].x) / 2, y: baseY - h * 0.35 },
    peaks[3],
    { x: (peaks[3].x + peaks[4].x) / 2, y: baseY - h * 0.25 },
    peaks[4],
    { x: cx + w / 2, y: baseY }, // 底右
  ];
  g.fillStyle(gold, 1);
  g.fillPoints(pts, true);
  g.lineStyle(2.5, outline, 1);
  g.strokePoints(pts, true);

  // 底座横条
  g.fillStyle(gold, 1);
  g.fillRoundedRect(cx - w / 2 - 4, baseY - 3, w + 8, baseH * 0.55, 4);
  g.lineStyle(2.5, outline, 1);
  g.strokeRoundedRect(cx - w / 2 - 4, baseY - 3, w + 8, baseH * 0.55, 4);

  // 底座中央红宝石
  g.fillStyle(ruby, 1);
  g.fillCircle(cx, baseY + baseH * 0.2, 4);
  g.lineStyle(1.5, goldDark, 1);
  g.strokeCircle(cx, baseY + baseH * 0.2, 4);
  g.fillStyle(0xffffff, 0.8);
  g.fillCircle(cx - 1, baseY + baseH * 0.2 - 1, 1);

  // 三个尖端宝石（中、左内、右内）
  const tipGems = [
    { p: peaks[2], color: ruby },
    { p: peaks[1], color: sapph },
    { p: peaks[3], color: sapph },
  ];
  tipGems.forEach(({ p, color }) => {
    g.fillStyle(color, 1);
    g.fillCircle(p.x, p.y + 2, 2.4);
    g.lineStyle(1, goldDark, 1);
    g.strokeCircle(p.x, p.y + 2, 2.4);
  });
}

// ─── 金边木箱 ───────────────────────────────────────────────────────
//
// 参考图 1 下方：木色主体 + 金色包边 + 提手 + 锁
function buildChestGoldTexture(scene: Phaser.Scene): void {
  const w = CHEST_TEX_W;
  const h = CHEST_TEX_H;
  const g = scene.make.graphics({ x: 0, y: 0 });

  const wood = 0x8a5a2a;
  const woodDark = 0x5a3818;
  const gold = 0xffd166;
  const goldDark = 0x9a6f1d;

  // 主体（圆角矩形 + 上半"盖子"用更浅色）
  g.fillStyle(wood, 1);
  g.fillRoundedRect(2, 8, w - 4, h - 10, 8);
  g.lineStyle(2, woodDark, 1);
  g.strokeRoundedRect(2, 8, w - 4, h - 10, 8);
  // 木板纵向纹理
  g.lineStyle(1, woodDark, 0.5);
  for (let x = 14; x < w - 4; x += 12) {
    g.beginPath();
    g.moveTo(x, 12);
    g.lineTo(x, h - 6);
    g.strokePath();
  }

  // 顶部金色包边
  g.fillStyle(gold, 1);
  g.fillRoundedRect(2, 8, w - 4, 12, 6);
  g.lineStyle(2, goldDark, 1);
  g.strokeRoundedRect(2, 8, w - 4, 12, 6);

  // 提手（半圆）
  g.lineStyle(3, goldDark, 1);
  const handleW = w * 0.32;
  g.beginPath();
  g.arc(w / 2, 10, handleW / 2, Math.PI, 0, false);
  g.strokePath();

  // 四角金块
  const corner = 10;
  g.fillStyle(gold, 1);
  g.fillRect(2, h - corner - 3, corner, corner);
  g.fillRect(w - corner - 2, h - corner - 3, corner, corner);
  g.lineStyle(1.5, goldDark, 1);
  g.strokeRect(2, h - corner - 3, corner, corner);
  g.strokeRect(w - corner - 2, h - corner - 3, corner, corner);

  // 中央锁
  const lockW = 14;
  const lockH = 18;
  g.fillStyle(gold, 1);
  g.fillRoundedRect((w - lockW) / 2, h / 2 - 2, lockW, lockH, 3);
  g.lineStyle(1.5, goldDark, 1);
  g.strokeRoundedRect((w - lockW) / 2, h / 2 - 2, lockW, lockH, 3);
  g.fillStyle(woodDark, 1);
  g.fillCircle(w / 2, h / 2 + 5, 2);

  g.generateTexture('chest-gold', w, h);
  g.destroy();
}

// ─── 元素 buff 箱（fire / ice / chain）─────────────────────────────────
//
// 与 chest-gold 同尺寸，但主体颜色随元素 + 中央画对应图标，让玩家一眼区分
// 普通宝箱（金）= 升级 / 元素箱（彩色）= 限时弹药
//
// 三种箱子分别注册为 'chest-fire' / 'chest-ice' / 'chest-chain'

interface BuffChestPalette {
  body: number;       // 主体颜色
  bodyDark: number;   // 边线
  trim: number;       // 顶部装饰条
  trimDark: number;
  iconColor: number;  // 中央图标颜色
}

function buildBuffChestTextures(scene: Phaser.Scene): void {
  // 火焰：橙红 + 黄火苗
  buildOneBuffChest(scene, 'chest-fire', {
    body: 0x6a1a0a, bodyDark: 0x2a0606,
    trim: 0xff6a3c, trimDark: 0xa03010,
    iconColor: 0xffe066,
  }, drawFireIcon);
  // 冰冻：深青 + 白雪花
  buildOneBuffChest(scene, 'chest-ice', {
    body: 0x0a3a4a, bodyDark: 0x041820,
    trim: 0x6ad6ff, trimDark: 0x208090,
    iconColor: 0xe8f8ff,
  }, drawIceIcon);
  // 闪电链：暗紫 + 亮紫闪电
  buildOneBuffChest(scene, 'chest-chain', {
    body: 0x3a0a4a, bodyDark: 0x180428,
    trim: 0xc0a8ff, trimDark: 0x6040a0,
    iconColor: 0xfff080,
  }, drawChainIcon);
}

function buildOneBuffChest(
  scene: Phaser.Scene,
  key: string,
  p: BuffChestPalette,
  drawIcon: (g: Phaser.GameObjects.Graphics, cx: number, cy: number, color: number) => void,
): void {
  const w = CHEST_TEX_W;
  const h = CHEST_TEX_H;
  const g = scene.make.graphics({ x: 0, y: 0 });

  // 主体（圆角矩形）
  g.fillStyle(p.body, 1);
  g.fillRoundedRect(2, 8, w - 4, h - 10, 8);
  g.lineStyle(2, p.bodyDark, 1);
  g.strokeRoundedRect(2, 8, w - 4, h - 10, 8);

  // 木板纵向纹理（弱化，让中央图标突出）
  g.lineStyle(1, p.bodyDark, 0.4);
  for (let x = 14; x < w - 4; x += 14) {
    g.beginPath();
    g.moveTo(x, 12);
    g.lineTo(x, h - 6);
    g.strokePath();
  }

  // 顶部装饰条（彩色）
  g.fillStyle(p.trim, 1);
  g.fillRoundedRect(2, 8, w - 4, 12, 6);
  g.lineStyle(2, p.trimDark, 1);
  g.strokeRoundedRect(2, 8, w - 4, 12, 6);

  // 提手（半圆，描边色）
  g.lineStyle(3, p.trimDark, 1);
  const handleW = w * 0.32;
  g.beginPath();
  g.arc(w / 2, 10, handleW / 2, Math.PI, 0, false);
  g.strokePath();

  // 四角彩色块（强调元素色）
  const corner = 9;
  g.fillStyle(p.trim, 1);
  g.fillRect(2, h - corner - 3, corner, corner);
  g.fillRect(w - corner - 2, h - corner - 3, corner, corner);
  g.lineStyle(1.5, p.trimDark, 1);
  g.strokeRect(2, h - corner - 3, corner, corner);
  g.strokeRect(w - corner - 2, h - corner - 3, corner, corner);

  // 中央图标（每种 buff 不同形状）
  drawIcon(g, w / 2, h / 2 + 6, p.iconColor);

  g.generateTexture(key, w, h);
  g.destroy();
}

/** 火苗图标：底宽顶尖的水滴形 + 内层亮黄 */
function drawFireIcon(g: Phaser.GameObjects.Graphics, cx: number, cy: number, color: number): void {
  // 外层深橙
  g.fillStyle(0xff6a3c, 1);
  g.beginPath();
  g.moveTo(cx, cy - 14);              // 顶点
  g.lineTo(cx + 10, cy - 2);          // 右上
  g.lineTo(cx + 8, cy + 10);          // 右下
  g.lineTo(cx, cy + 14);              // 底
  g.lineTo(cx - 8, cy + 10);
  g.lineTo(cx - 10, cy - 2);
  g.closePath();
  g.fillPath();
  g.lineStyle(1.5, 0xa03010, 1);
  g.strokePath();
  // 内层亮色（小一圈）
  g.fillStyle(color, 1);
  g.beginPath();
  g.moveTo(cx, cy - 8);
  g.lineTo(cx + 5, cy);
  g.lineTo(cx + 4, cy + 6);
  g.lineTo(cx, cy + 9);
  g.lineTo(cx - 4, cy + 6);
  g.lineTo(cx - 5, cy);
  g.closePath();
  g.fillPath();
}

/** 雪花图标：6 角 + 中心点 */
function drawIceIcon(g: Phaser.GameObjects.Graphics, cx: number, cy: number, color: number): void {
  g.lineStyle(2.4, color, 1);
  const r = 13;
  // 6 条放射线（每隔 60°）
  for (let i = 0; i < 6; i++) {
    const a = (Math.PI * 2 * i) / 6 - Math.PI / 2;
    g.beginPath();
    g.moveTo(cx, cy);
    g.lineTo(cx + Math.cos(a) * r, cy + Math.sin(a) * r);
    g.strokePath();
  }
  // 每条线尾端"小叉"提示雪花感
  g.lineStyle(1.6, color, 1);
  for (let i = 0; i < 6; i++) {
    const a = (Math.PI * 2 * i) / 6 - Math.PI / 2;
    const ex = cx + Math.cos(a) * r;
    const ey = cy + Math.sin(a) * r;
    const fork = 4;
    const a1 = a + Math.PI / 6;
    const a2 = a - Math.PI / 6;
    g.beginPath();
    g.moveTo(ex, ey);
    g.lineTo(ex - Math.cos(a1) * fork, ey - Math.sin(a1) * fork);
    g.strokePath();
    g.beginPath();
    g.moveTo(ex, ey);
    g.lineTo(ex - Math.cos(a2) * fork, ey - Math.sin(a2) * fork);
    g.strokePath();
  }
  // 中心圆点
  g.fillStyle(color, 1);
  g.fillCircle(cx, cy, 2);
}

/** 闪电图标：Z 字形折线 */
function drawChainIcon(g: Phaser.GameObjects.Graphics, cx: number, cy: number, color: number): void {
  g.fillStyle(color, 1);
  g.lineStyle(1.5, 0x6040a0, 1);
  g.beginPath();
  g.moveTo(cx - 2, cy - 14);   // 顶
  g.lineTo(cx + 7, cy - 2);    // 右上凸
  g.lineTo(cx + 1, cy - 1);
  g.lineTo(cx + 5, cy + 14);   // 底（略偏右）
  g.lineTo(cx - 6, cy + 2);    // 左下凸
  g.lineTo(cx - 1, cy + 1);
  g.closePath();
  g.fillPath();
  g.strokePath();
}

// ─── 战场地块 ────────────────────────────────────────────────────────
//
// 焦土 + 泥地 + 弹坑：给整局战场一个更明确的战争氛围。
// TileSprite 会滚动这张纹理，所以图案保持低对比，避免干扰子弹/敌人读形。
function buildGrassTileTexture(scene: Phaser.Scene): void {
  const size = 64; // 单块边长，做 TileSprite 时会自动平铺
  const g = scene.make.graphics({ x: 0, y: 0 });

  // 底色：橄榄灰泥地，局部焦黑块制造战场脏污感
  g.fillStyle(0x2f3528, 1);
  g.fillRect(0, 0, size, size);
  g.fillStyle(0x3d3a2b, 0.9);
  g.fillRect(0, 0, size, 18);
  g.fillStyle(0x252820, 0.65);
  g.fillRect(0, 42, size, 22);

  // 横向车辙/履带印：滚动时像战场地面在后退
  g.lineStyle(2, 0x1d211b, 0.55);
  g.beginPath();
  g.moveTo(8, 0);
  g.lineTo(8, size);
  g.moveTo(56, 0);
  g.lineTo(56, size);
  g.strokePath();
  g.lineStyle(1, 0x57513b, 0.32);
  for (let y = 6; y < size; y += 12) {
    g.beginPath();
    g.moveTo(5, y);
    g.lineTo(13, y + 4);
    g.moveTo(51, y + 4);
    g.lineTo(59, y);
    g.strokePath();
  }

  // 弹坑：暗外圈 + 亮边，低 alpha 不抢主体
  const craters = [
    { x: 24, y: 22, rx: 10, ry: 6 },
    { x: 46, y: 50, rx: 8, ry: 5 },
  ];
  for (const c of craters) {
    g.fillStyle(0x12140f, 0.42);
    g.fillEllipse(c.x, c.y, c.rx * 2, c.ry * 2);
    g.lineStyle(1, 0x6f6244, 0.24);
    g.strokeEllipse(c.x - 1, c.y - 1, c.rx * 2, c.ry * 2);
  }

  // 少量碎石/火星
  g.fillStyle(0x8a6a3a, 0.5);
  g.fillCircle(14, 34, 1.4);
  g.fillCircle(34, 10, 1.2);
  g.fillCircle(58, 28, 1.1);
  g.fillStyle(0xff8a3c, 0.35);
  g.fillCircle(18, 56, 1.2);
  g.fillCircle(40, 36, 0.9);

  g.generateTexture('grass-tile', size, size);
  g.destroy();
}

function buildObstacleTextures(scene: Phaser.Scene): void {
  buildSandbagTexture(scene);
  buildRubbleTexture(scene);
  buildBarricadeTexture(scene);
}

function buildSandbagTexture(scene: Phaser.Scene): void {
  const w = 86;
  const h = 38;
  const g = scene.make.graphics({ x: 0, y: 0 });
  g.fillStyle(0x000000, 0.18);
  g.fillEllipse(w / 2, h - 5, w * 0.82, 10);
  const bags = [
    { x: 14, y: 18, ww: 28, hh: 14 },
    { x: 40, y: 18, ww: 30, hh: 15 },
    { x: 66, y: 18, ww: 28, hh: 14 },
    { x: 28, y: 9, ww: 30, hh: 14 },
    { x: 56, y: 9, ww: 30, hh: 14 },
  ];
  for (const b of bags) {
    g.fillStyle(0x80663c, 1);
    g.fillRoundedRect(b.x - b.ww / 2, b.y - b.hh / 2, b.ww, b.hh, 6);
    g.lineStyle(1.5, 0x3a2a18, 0.9);
    g.strokeRoundedRect(b.x - b.ww / 2, b.y - b.hh / 2, b.ww, b.hh, 6);
    g.lineStyle(1, 0xb69a62, 0.35);
    g.beginPath();
    g.moveTo(b.x - b.ww * 0.28, b.y);
    g.lineTo(b.x + b.ww * 0.28, b.y);
    g.strokePath();
  }
  g.generateTexture('obstacle-sandbag', w, h);
  g.destroy();
}

function buildRubbleTexture(scene: Phaser.Scene): void {
  const w = 70;
  const h = 48;
  const g = scene.make.graphics({ x: 0, y: 0 });
  g.fillStyle(0x000000, 0.18);
  g.fillEllipse(w / 2, h - 8, w * 0.72, 12);
  const rocks = [
    { x: 18, y: 28, r: 10, c: 0x50483a },
    { x: 34, y: 24, r: 14, c: 0x3b3a34 },
    { x: 52, y: 30, r: 9, c: 0x66583c },
    { x: 30, y: 14, r: 8, c: 0x2a2b26 },
    { x: 44, y: 16, r: 7, c: 0x766346 },
  ];
  for (const r of rocks) {
    g.fillStyle(r.c, 1);
    g.fillCircle(r.x, r.y, r.r);
    g.lineStyle(1.5, 0x171914, 0.75);
    g.strokeCircle(r.x, r.y, r.r);
  }
  g.fillStyle(0xff8a3c, 0.35);
  g.fillCircle(24, 34, 1.5);
  g.fillCircle(48, 22, 1.1);
  g.generateTexture('obstacle-rubble', w, h);
  g.destroy();
}

function buildBarricadeTexture(scene: Phaser.Scene): void {
  const w = 88;
  const h = 54;
  const g = scene.make.graphics({ x: 0, y: 0 });
  g.fillStyle(0x000000, 0.2);
  g.fillEllipse(w / 2, h - 8, w * 0.78, 10);
  g.lineStyle(8, 0x6a411d, 1);
  g.beginPath();
  g.moveTo(18, 38);
  g.lineTo(70, 14);
  g.moveTo(18, 14);
  g.lineTo(70, 38);
  g.strokePath();
  g.lineStyle(2, 0x2a180c, 0.95);
  g.beginPath();
  g.moveTo(18, 38);
  g.lineTo(70, 14);
  g.moveTo(18, 14);
  g.lineTo(70, 38);
  g.strokePath();
  g.fillStyle(0xffd166, 0.75);
  g.fillTriangle(10, 40, 18, 24, 26, 40);
  g.fillTriangle(62, 40, 70, 24, 78, 40);
  g.generateTexture('obstacle-barricade', w, h);
  g.destroy();
}
